/**
 * Tri-state helpers for the per-drop shortlink options (click-id structure,
 * domain scheme) across single, bulk and sent edits.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Unlike useOnlyNewDomains these stay editable on sent/scheduled drops, so
 * every edit path treats them like forceSend: a concrete pick is sent,
 * 'unchanged' / 'mixed' are omitted so each drop keeps its own value.
 */

import type { ShortlinkClickIdStructure, ShortlinkDomainScheme } from '@trafficpulse/shared';

import type { ShortlinkOptionValue } from '@/features/smswiser-campaigns/components/drop-modals';

export type ClickIdStructureValue = ShortlinkOptionValue<ShortlinkClickIdStructure>;
export type DomainSchemeValue = ShortlinkOptionValue<ShortlinkDomainScheme>;

/** The concrete pick, or undefined for "keep each drop's own" / "drops disagree". */
export function shortlinkPick<T extends string>(v: ShortlinkOptionValue<T>): T | undefined {
  return v === 'unchanged' || v === 'mixed' ? undefined : v;
}

/** True when the selected drops disagree (absent values already normalized to the default). */
export function isShortlinkMixed<T extends string>(values: T[]): boolean {
  return new Set(values).size > 1;
}

/**
 * Picker seed: single edit shows the drop's own value; bulk starts at 'mixed'
 * when drops disagree, otherwise 'unchanged' (mirrors forceSend/useOnlyNewDomains).
 */
export function initialShortlinkValue<T extends string>(
  isSingleEdit: boolean,
  value: T,
  mixed: boolean | undefined,
): ShortlinkOptionValue<T> {
  if (mixed) return 'mixed';
  return isSingleEdit ? value : 'unchanged';
}
